import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import { articles } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-200">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500 mb-4">文章未找到</p>
            <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium text-sm cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://zhainanvideo.com/' },
      { '@type': 'ListItem', position: 2, name: article.category },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    image: article.cover,
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navbar />

      <main className="pt-16">
        <article className="bg-background-50">
          <div className="w-full px-4 md:px-6 py-8 md:py-12">
            <div className="max-w-4xl mx-auto">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line text-xs"></i>
                </div>
                <span className="text-foreground-400">{article.category}</span>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line text-xs"></i>
                </div>
                <span className="text-foreground-800 font-medium truncate">{article.title}</span>
              </nav>

              <header className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <span className="px-2.5 py-1 bg-accent-100 text-accent-800 text-xs font-medium rounded whitespace-nowrap">
                    {article.category}
                  </span>
                  <span className="text-xs text-foreground-400 whitespace-nowrap">{article.date}</span>
                  <span className="text-xs text-foreground-400 whitespace-nowrap">{article.readTime} 阅读</span>
                </div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
                  {article.title}
                </h1>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-user-line text-sm"></i>
                  </div>
                  <span className="text-sm text-foreground-600 font-medium">{article.author}</span>
                </div>
              </header>

              <div className="relative w-full aspect-[16/9] rounded-xl overflow-hidden mb-8">
                <img
                  src={article.cover}
                  alt={article.title}
                  title={article.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed">
                {article.content.split('\n\n').map((paragraph, idx) => (
                  <p key={idx} className="mb-4 text-sm md:text-base leading-relaxed">
                    {paragraph.trim()}
                  </p>
                ))}
              </div>

              <div className="border-t border-background-200/60 mt-10 pt-6">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-line"></i>
                  </div>
                  返回首页
                </Link>
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}